import React, { useState, useMemo } from "react";
import {
  Typography,
  Box,
  Divider,
  Chip,
  IconButton,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  TableContainerProps,
  Button,
} from "@mui/material";
import { FiX, FiLock } from "react-icons/fi";
import { RiLock2Fill } from "react-icons/ri";
import { FaFutbol } from "react-icons/fa";
import { MatchData } from "@/interfaces/liveGamesType";
import { isMatchLive } from "../../utils/index";
import HistoricalTrends from "../HistoricalTrends";
import MatchPredictions from "../MatchPredictions";
import AttackMomentum from "@/components/AttackMomentum";
import { translatedStats } from "@/constants/footballStats";
import NeonTabs, { TabPanel, a11yProps } from "@/components/NeonTabs";
import styled, { keyframes } from "styled-components";
import { useAuth } from "@/context/AuthContext";
import { fakeMatchData } from "./fakeData";
import NeonButton from "@/components/NeonButton";
import { useNavigate } from "react-router-dom";
import { UseUser } from "@/context/UserContext";

const pulseBorder = keyframes`
  0% {
    border-color: rgba(42, 244, 151, 0.3);
  }
  50% {
    border-color: rgba(42, 244, 151, 0.8);
  }
  100% {
    border-color: rgba(42, 244, 151, 0.3);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const liveTextBlink = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const rotateGlow = keyframes`
  0% {
    box-shadow: 0 0 8px rgba(42, 244, 151, 0.5), 0 0 16px rgba(42, 244, 151, 0.3);
    border-color: rgba(42, 244, 151, 0.5);
  }
  50% {
    box-shadow: 0 0 16px rgba(42, 244, 151, 0.8), 0 0 24px rgba(42, 244, 151, 0.5);
    border-color: rgba(42, 244, 151, 0.8);
  }
  100% {
    box-shadow: 0 0 8px rgba(42, 244, 151, 0.5), 0 0 16px rgba(42, 244, 151, 0.3);
    border-color: rgba(42, 244, 151, 0.5);
  }
`;

const footballSpin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const lock3DEffect = keyframes`
  0%, 100% {
    transform: scale(1) translateZ(0) translateY(0px) perspective(120px) rotateY(-12deg) ;
    filter: drop-shadow(0 0 15px rgba(255, 87, 87, 0.6)) drop-shadow(0 8px 10px rgba(0, 0, 0, 0.4));
    text-shadow: 0 0 10px rgba(255, 87, 87, 0.7), 0 0 20px rgba(255, 87, 87, 0.5);
  }
  50% {
    transform: scale(1.05) translateZ(500px) translateY(-7px) perspective(120px) rotateY(12deg);
    filter: drop-shadow(0 0 25px rgba(255, 87, 87, 0.8)) drop-shadow(0 15px 15px rgba(0, 0, 0, 0.3));
    text-shadow: 0 0 20px rgba(255, 87, 87, 0.9), 0 0 30px rgba(255, 87, 87, 0.7);
  }
`;

// Componentes estilizados
const NeonMatchDetailsContainer = styled(Box)`
  height: 100%;
  overflow-y: auto;
  padding: 16px;
  background: rgba(20, 20, 25, 0.7);
  border: 1px solid rgba(42, 244, 151, 0.2);
  backdrop-filter: blur(5px);
  border-radius: 8px;
`;

const NeonTeamScore = styled(Typography)`
  font-size: 2.5rem;
  font-weight: bold;
  color: rgba(42, 244, 151, 0.9);
  text-shadow: 0 0 10px rgba(42, 244, 151, 0.7);
`;

const NeonDivider = styled(Divider)`
  background: linear-gradient(
    90deg,
    rgba(42, 244, 151, 0.1),
    rgba(42, 244, 151, 0.8),
    rgba(42, 244, 151, 0.1)
  );
  height: 2px;
  margin: 16px 0;
  box-shadow: 0 0 8px rgba(42, 244, 151, 0.5);
`;

const NeonText = styled(Typography)`
  color: #fff;
  text-shadow: 0 0 3px rgba(42, 244, 151, 0.3);
`;

const NeonTitleText = styled(Typography)`
  color: rgba(42, 244, 151, 0.9);
  text-shadow: 0 0 5px rgba(42, 244, 151, 0.5);
  font-weight: 500;
  letter-spacing: 0.5px;
`;

const NeonChip = styled(Chip)`
  background: rgba(42, 244, 151, 0.1);
  border: 1px solid rgba(42, 244, 151, 0.3);
  color: rgba(42, 244, 151, 0.9);
  text-shadow: 0 0 3px rgba(42, 244, 151, 0.3);
  animation: ${pulseBorder} 3s infinite;

  &:hover {
    background: rgba(42, 244, 151, 0.2);
  }
`;

const NeonScoreBox = styled(Box)`
  background: rgba(20, 20, 25, 0.6);
  border: 1px solid rgba(42, 244, 151, 0.3);
  border-radius: 8px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 15px rgba(42, 244, 151, 0.3);
  animation: ${rotateGlow} 4s infinite;
`;

const NeonTeamLogo = styled.img`
  filter: drop-shadow(0 0 5px rgba(42, 244, 151, 0.5));
  transition: all 0.3s ease;

  &:hover {
    filter: drop-shadow(0 0 8px rgba(42, 244, 151, 0.7));
    transform: scale(1.1);
  }
`;

// Fixed with explicit type for width prop
const NeonStatsProgressHome = styled(Box)(
  ({ width }: { width: string }) => `
    width: ${width};
    background: linear-gradient(90deg, rgba(42, 244, 151, 0.8), rgba(42, 244, 151, 0.4));
    height: 8px;
    border-radius: 4px 0 0 4px;
    box-shadow: 0 0 8px rgba(42, 244, 151, 0.4);
  `
);

// Fixed with explicit type for width prop
const NeonStatsProgressAway = styled(Box)(
  ({ width }: { width: string }) => `
    width: ${width};
    background: linear-gradient(90deg, rgba(255, 87, 87, 0.4), rgba(255, 87, 87, 0.8));
    height: 8px;
    border-radius: 0 4px 4px 0;
    box-shadow: 0 0 8px rgba(255, 87, 87, 0.4);
  `
);

interface TableContainerComponentProps extends TableContainerProps {
  variant?: string;
}

const NeonTableContainer = styled(TableContainer)<TableContainerComponentProps>`
  background: rgba(20, 20, 25, 0.7);
  border: 1px solid rgba(42, 244, 151, 0.2);
  border-radius: 8px;

  .MuiTable-root {
    background: transparent;
  }

  .MuiTableHead-root {
    .MuiTableCell-root {
      background-color: rgba(42, 244, 151, 0.1);
      color: rgba(42, 244, 151, 0.9);
      font-weight: bold;
      border-bottom: 1px solid rgba(42, 244, 151, 0.3);
    }
  }

  .MuiTableBody-root {
    .MuiTableCell-root {
      border-bottom: 1px solid rgba(42, 244, 151, 0.1);
      color: white;
    }

    .MuiTableRow-root:hover {
      background-color: rgba(42, 244, 151, 0.05);
    }
  }
`;

const NeonFootballIcon = styled(FaFutbol)`
  color: rgba(42, 244, 151, 0.9);
  animation: ${footballSpin} 5s linear infinite;
`;

const NeonCloseButton = styled(IconButton)`
  color: rgba(42, 244, 151, 0.9);
  transition: all 0.3s ease;

  &:hover {
    color: rgba(42, 244, 151, 1);
    transform: scale(1.1);
    box-shadow: 0 0 15px rgba(42, 244, 151, 0.5);
  }
`;

const LiveIndicator = styled(Typography)`
  color: rgba(42, 244, 151, 1);
  font-weight: bold;
  animation: ${liveTextBlink} 1.5s infinite;
  text-shadow: 0 0 5px rgba(42, 244, 151, 0.7);
`;

const LockedContentContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 24px;
  background: rgba(20, 20, 25, 0.7);
  border: 1px solid rgba(255, 87, 87, 0.3);
  border-radius: 8px;
  text-align: center;
`;

const LockIcon = styled(RiLock2Fill)`
  font-size: 80px;
  color: rgba(255, 87, 87, 1);
  margin-bottom: 20px;
  filter: drop-shadow(0 0 15px rgba(255, 87, 87, 0.7))
    drop-shadow(0 10px 10px rgba(0, 0, 0, 0.5))
    drop-shadow(4px 4px 5px rgba(0, 0, 0, 0.3));
  animation: ${lock3DEffect} 4s ease-in-out infinite;
  transform-style: preserve-3d;
  transform: perspective(150px);
  position: relative;

  &:after {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    top: 5px;
    left: 5px;
    background: rgba(255, 20, 20, 0.3);
    filter: blur(10px);
    z-index: -1;
    border-radius: 8px;
  }
`;

// Adicionar styled component para o container com blur
const BlurredContentOverlay = styled(Box)`
  position: relative;
  width: 100%;
  height: 100%;
`;

const BlurredContent = styled(Box)`
  filter: blur(15px);
  opacity: 0.7;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const ContentOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(20, 20, 25, 0.4);
  z-index: 5;
  border-radius: 8px;
`;

const LockedContentMessage = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 90%;
  max-width: 400px;
  padding: 24px;
`;

interface MatchDetailsProps {
  match: MatchData | null;
  onClose?: () => void;
  isMobile?: boolean;
}

const MatchDetails: React.FC<MatchDetailsProps> = ({
  match,
  onClose,
  isMobile = false,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const { isAuthenticated, decodedToken } = useAuth();
  const {setPaymentModal} = UseUser()
  const navigate = useNavigate()

  const matchId = match?.match_id || "";
  const matchIsLive = match ? isMatchLive(match) : false;
  const refetchIntervalTime = matchIsLive ? 60000 : false; // 1 minuto para partidas ao vivo

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Renderizar mensagem quando não há partida selecionada
  if (!match) {
    return (
      <NeonMatchDetailsContainer
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <NeonText variant="body1">
          Selecione uma partida para ver os detalhes
        </NeonText>
      </NeonMatchDetailsContainer>
    );
  }

  const LockComponent = () => (
    <LockedContentMessage>
      <LockIcon />
      <NeonText variant="h6">Conteúdo Bloqueado</NeonText>
      <NeonText variant="body1" sx={{ my: 2 }}>
        {isAuthenticated
          ? "Para acessar, você precisa adquirir um plano."
          : "Para acessar, você precisa se registrar adquirir um plano."}
      </NeonText>
      <NeonButton onClick={() => isAuthenticated ? setPaymentModal(true) : navigate("register?af=1")} style={{ borderRadius: "6px" }}>
        {isAuthenticated ? "Adquirir Plano" : "Registrar-se"}
      </NeonButton>
    </LockedContentMessage>
  );

  // Renderizar estatística com barra de progresso
  const renderStatWithProgress = (
    label: string,
    homeValue: string | number,
    awayValue: string | number
  ) => {
    const homeNum =
      typeof homeValue === "string" ? parseInt(homeValue, 10) || 0 : homeValue;
    const awayNum =
      typeof awayValue === "string" ? parseInt(awayValue, 10) || 0 : awayValue;
    const total = homeNum + awayNum;
    const homePercent = total > 0 ? (homeNum / total) * 100 : 50;
    const awayPercent = total > 0 ? (awayNum / total) * 100 : 50;

    return (
      <Box sx={{ mb: 2 }}>
        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <NeonText variant="body2" fontWeight="bold">
            {homeNum}
          </NeonText>
          <NeonText variant="body2">{label}</NeonText>
          <NeonText variant="body2" fontWeight="bold">
            {awayNum}
          </NeonText>
        </Box>
        <Box display="flex" alignItems="center">
          <NeonStatsProgressHome width={`${homePercent}%`} />
          <NeonStatsProgressAway width={`${awayPercent}%`} />
        </Box>
      </Box>
    );
  };

  // Renderizar eventos da partida (gols, cartões)
  const renderMatchEvents = () => {
    const events = [];

    // Adicionar gols
    if (match.goalscorer && match.goalscorer.length > 0) {
      events.push(
        <Box key="goals" mb={2}>
          <NeonTitleText variant="subtitle2" gutterBottom>
            Gols
          </NeonTitleText>
          {match.goalscorer.map((goal, index) => (
            <Box
              key={`goal-${index}`}
              display="flex"
              alignItems="center"
              mb={1}
              sx={{
                padding: "8px",
                borderRadius: "4px",
                background: "rgba(42, 244, 151, 0.05)",
                border: "1px solid rgba(42, 244, 151, 0.1)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "rgba(42, 244, 151, 0.1)",
                  transform: "translateX(5px)",
                },
              }}
            >
              <NeonFootballIcon style={{ marginRight: 8 }} />
              <NeonText variant="body2">
                {goal.time}' - {goal.home_scorer || goal.away_scorer}
                {goal.home_assist || goal.away_assist
                  ? ` (Assist: ${goal.home_assist || goal.away_assist})`
                  : ""}
              </NeonText>
            </Box>
          ))}
        </Box>
      );
    }

    // Adicionar cartões
    if (match.cards && match.cards.length > 0) {
      events.push(
        <Box key="cards" mb={2}>
          <NeonTitleText variant="subtitle2" gutterBottom>
            Cartões
          </NeonTitleText>
          {match.cards.map((card, index) => (
            <Box
              key={`card-${index}`}
              display="flex"
              alignItems="center"
              mb={1}
              sx={{
                padding: "8px",
                borderRadius: "4px",
                background: "rgba(42, 244, 151, 0.05)",
                border: "1px solid rgba(42, 244, 151, 0.1)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "rgba(42, 244, 151, 0.1)",
                  transform: "translateX(5px)",
                },
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 16,
                  bgcolor: card.card.toLowerCase().includes("yellow")
                    ? "yellow"
                    : "red",
                  mr: 1,
                  boxShadow: card.card.toLowerCase().includes("yellow")
                    ? "0 0 5px rgba(255, 255, 0, 0.7)"
                    : "0 0 5px rgba(255, 0, 0, 0.7)",
                }}
              />
              <NeonText variant="body2">
                {card.time}' - {card.home_fault || card.away_fault}
              </NeonText>
            </Box>
          ))}
        </Box>
      );
    }

    if (events.length === 0) {
      return (
        <NeonText variant="body2" align="center">
          Nenhum evento registrado
        </NeonText>
      );
    }

    return events;
  };

  // Renderizar estatísticas da partida
  const renderMatchStatistics = () => {
    if (!match.statistics || match.statistics.length === 0) {
      return (
        <NeonText variant="body2" align="center">
          Estatísticas não disponíveis para esta partida
        </NeonText>
      );
    }

    // Estatísticas principais para exibir no topo
    const mainStats = [
      "Posse de Bola",
      "Chutes a Gol",
      "Chutes Fora",
      "Escanteios",
      "Faltas",
    ];

    return (
      <>
        <Box mb={3}>
          {match.statistics.map((stat, index) => {
            const statName = stat.type;
            if (mainStats.includes(statName)) {
              return (
                <Box key={`stat-${index}`} mb={2}>
                  {renderStatWithProgress(statName, stat.home, stat.away)}
                </Box>
              );
            }
            return null;
          })}
        </Box>

        <NeonTableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">
                  {match.match_hometeam_name}
                </TableCell>
                <TableCell align="center">Estatística</TableCell>
                <TableCell align="center">
                  {match.match_awayteam_name}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {match.statistics.map((stat, index) => (
                <TableRow key={`stat-detail-${index}`}>
                  <TableCell align="center">{stat.home}</TableCell>
                  <TableCell align="center">
                    {translatedStats[
                      stat.type as keyof typeof translatedStats
                    ] || stat.type}
                  </TableCell>
                  <TableCell align="center">{stat.away}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </NeonTableContainer>
      </>
    );
  };

  const renderLineups = () => {
    const hasLineups =
      match.lineup &&
      match.lineup.home &&
      match.lineup.home.starting_lineups &&
      match.lineup.home.starting_lineups.length > 0;

    if (!hasLineups) {
      return (
        <NeonText variant="body2" align="center">
          Escalações não disponíveis para esta partida
        </NeonText>
      );
    }

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <NeonTitleText variant="subtitle1" gutterBottom>
            {match.match_hometeam_name}
          </NeonTitleText>
          <Box
            sx={{
              p: 2,
              borderRadius: "8px",
              background: "rgba(42, 244, 151, 0.03)",
              border: "1px solid rgba(42, 244, 151, 0.1)",
            }}
          >
            <NeonText variant="subtitle2" gutterBottom>
              Titulares
            </NeonText>
            {match.lineup.home.starting_lineups.map((player, index) => (
              <Box
                key={`home-player-${index}`}
                display="flex"
                alignItems="center"
                mb={1}
              >
                <NeonText variant="body2">
                  {player.lineup_number}. {player.lineup_player}
                  {player.lineup_position ? ` (${player.lineup_position})` : ""}
                </NeonText>
              </Box>
            ))}

            {match.lineup.home.substitutes &&
              match.lineup.home.substitutes.length > 0 && (
                <>
                  <NeonDivider />
                  <NeonText variant="subtitle2" gutterBottom>
                    Substitutos
                  </NeonText>
                  {match.lineup.home.substitutes.map((player, index) => (
                    <Box
                      key={`home-sub-${index}`}
                      display="flex"
                      alignItems="center"
                      mb={1}
                    >
                      <NeonText variant="body2">
                        {player.lineup_number}. {player.lineup_player}
                        {player.lineup_position
                          ? ` (${player.lineup_position})`
                          : ""}
                      </NeonText>
                    </Box>
                  ))}
                </>
              )}

            {match.lineup.home.coach && match.lineup.home.coach.length > 0 && (
              <>
                <NeonDivider />
                <NeonText variant="subtitle2" gutterBottom>
                  Técnico
                </NeonText>
                <NeonText variant="body2">
                  {match.lineup.home.coach[0].lineup_player}
                </NeonText>
              </>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <NeonTitleText variant="subtitle1" gutterBottom>
            {match.match_awayteam_name}
          </NeonTitleText>
          <Box
            sx={{
              p: 2,
              borderRadius: "8px",
              background: "rgba(42, 244, 151, 0.03)",
              border: "1px solid rgba(42, 244, 151, 0.1)",
            }}
          >
            <NeonText variant="subtitle2" gutterBottom>
              Titulares
            </NeonText>
            {match.lineup.away.starting_lineups.map((player, index) => (
              <Box
                key={`away-player-${index}`}
                display="flex"
                alignItems="center"
                mb={1}
              >
                <NeonText variant="body2">
                  {player.lineup_number}. {player.lineup_player}
                  {player.lineup_position ? ` (${player.lineup_position})` : ""}
                </NeonText>
              </Box>
            ))}

            {match.lineup.away.substitutes &&
              match.lineup.away.substitutes.length > 0 && (
                <>
                  <NeonDivider />
                  <NeonText variant="subtitle2" gutterBottom>
                    Substitutos
                  </NeonText>
                  {match.lineup.away.substitutes.map((player, index) => (
                    <Box
                      key={`away-sub-${index}`}
                      display="flex"
                      alignItems="center"
                      mb={1}
                    >
                      <NeonText variant="body2">
                        {player.lineup_number}. {player.lineup_player}
                        {player.lineup_position
                          ? ` (${player.lineup_position})`
                          : ""}
                      </NeonText>
                    </Box>
                  ))}
                </>
              )}

            {match.lineup.away.coach && match.lineup.away.coach.length > 0 && (
              <>
                <NeonDivider />
                <NeonText variant="subtitle2" gutterBottom>
                  Técnico
                </NeonText>
                <NeonText variant="body2">
                  {match.lineup.away.coach[0].lineup_player}
                </NeonText>
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderPredictions = () => {
    if (!isAuthenticated || !decodedToken.isActive) {
      return (
        <BlurredContentOverlay>
          <BlurredContent>
            <MatchPredictions match={fakeMatchData} isLoading={false} />
          </BlurredContent>
          <ContentOverlay />
          <LockComponent />
        </BlurredContentOverlay>
      );
    }
    return <MatchPredictions match={match} />;
  };

  const renderVisualizations = () => {
    if (!isAuthenticated || !decodedToken.isActive) {
      return (
        <BlurredContentOverlay>
          <BlurredContent>
            <Box sx={{ padding: "16px" }}>
              <NeonTitleText variant="subtitle1" gutterBottom>
                Momentum de Ataque
              </NeonTitleText>

              <AttackMomentum matchData={fakeMatchData} />

              <NeonDivider />

              <NeonTitleText variant="subtitle1" gutterBottom>
                Tendências Históricas
              </NeonTitleText>

              <HistoricalTrends match={fakeMatchData} />
            </Box>
          </BlurredContent>
          <ContentOverlay />
          <LockComponent />
        </BlurredContentOverlay>
      );
    }

    return (
      <Box>
        <NeonTitleText variant="subtitle1" gutterBottom>
          Momentum de Ataque
        </NeonTitleText>
        {matchId ? (
          <AttackMomentum matchData={match} />
        ) : (
          <NeonText variant="body2" align="center">
            Dados não disponíveis para visualização
          </NeonText>
        )}

        <NeonDivider />

        <NeonTitleText variant="subtitle1" gutterBottom>
          Tendências Históricas
        </NeonTitleText>
        {match ? (
          <HistoricalTrends match={match} />
        ) : (
          <NeonText variant="body2" align="center">
            Dados não disponíveis para visualização
          </NeonText>
        )}
      </Box>
    );
  };

  const getMatchStatusColor = () => {
    if (matchIsLive) return "rgba(42, 244, 151, 0.9)";
    if (match.match_status === "Finished") return "rgba(255, 87, 87, 0.9)";
    return "rgba(255, 255, 255, 0.7)";
  };

  return (
    <NeonMatchDetailsContainer>
      {/* Cabeçalho com botão de fechar para versão mobile */}
      {isMobile && onClose && (
        <Box display="flex" justifyContent="flex-end" mb={1}>
          <NeonCloseButton onClick={onClose} size="small">
            <FiX />
          </NeonCloseButton>
        </Box>
      )}

      {/* Cabeçalho da partida */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        mb={3}
      >
        <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
          <NeonChip label={match.league_name} size="small" sx={{ mb: 1 }} />
        </Box>

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          mb={1}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            flex={1}
          >
            <NeonTeamLogo
              src={match.team_home_badge}
              alt={match.match_hometeam_name}
              width={64}
              height={64}
              style={{ marginBottom: "8px" }}
              onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                e.currentTarget.src = `https://ui-avatars.com/api/?name=${match.match_hometeam_name}&background=2af497&color=121212`;
              }}
            />
            <NeonText variant="subtitle1" align="center">
              {match.match_hometeam_name}
            </NeonText>
          </Box>

          <NeonScoreBox mx={2}>
            <NeonTeamScore variant="h3">
              {match.match_hometeam_score}
            </NeonTeamScore>
            <NeonText variant="h4" sx={{ mx: 1 }}>
              -
            </NeonText>
            <NeonTeamScore variant="h3">
              {match.match_awayteam_score}
            </NeonTeamScore>
          </NeonScoreBox>

          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            flex={1}
          >
            <NeonTeamLogo
              src={match.team_away_badge}
              alt={match.match_awayteam_name}
              width={64}
              height={64}
              style={{ marginBottom: "8px" }}
              onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                e.currentTarget.src = `https://ui-avatars.com/api/?name=${match.match_awayteam_name}&background=2af497&color=121212`;
              }}
            />
            <NeonText variant="subtitle1" align="center">
              {match.match_awayteam_name}
            </NeonText>
          </Box>
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          mt={1}
        >
          {matchIsLive ? (
            <LiveIndicator variant="body2">
              • LIVE - {match.match_status}'
            </LiveIndicator>
          ) : (
            <NeonText variant="body2" sx={{ color: getMatchStatusColor() }}>
              {match.match_status} {match.match_date} {match.match_time}
            </NeonText>
          )}
          <NeonText variant="caption" sx={{ mt: 0.5 }}>
            Árbitro: {match.match_referee || "Não informado"}
          </NeonText>
          <NeonText variant="caption">
            Estádio: {match.match_stadium || "Não informado"}
          </NeonText>
        </Box>
      </Box>

      <NeonDivider />

      {/* Tabs de conteúdo */}
      <NeonTabs
        value={tabValue}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Estatísticas" {...a11yProps(0, "match")} />
        <Tab label="Eventos" {...a11yProps(1, "match")} />
        <Tab label="Escalações" {...a11yProps(2, "match")} />
        <Tab label="Previsões" {...a11yProps(3, "match")} />
        <Tab label="Visualizações" {...a11yProps(4, "match")} />
      </NeonTabs>

      <Box sx={{ mt: 2 }}>
        <TabPanel value={tabValue} index={0} prefix="match">
          {renderMatchStatistics()}
        </TabPanel>

        <TabPanel value={tabValue} index={1} prefix="match">
          {renderMatchEvents()}
        </TabPanel>

        <TabPanel value={tabValue} index={2} prefix="match">
          {renderLineups()}
        </TabPanel>

        <TabPanel value={tabValue} index={3} prefix="match">
          {renderPredictions()}
        </TabPanel>

        <TabPanel value={tabValue} index={4} prefix="match">
          {renderVisualizations()}
        </TabPanel>
      </Box>
    </NeonMatchDetailsContainer>
  );
};

export default MatchDetails;
